import type { AdBreak } from '../types'
import { fetchVast, parseVastXml } from './VastClient'

function parseOffset(raw: string, durationHint = 0): { type: AdBreak['type']; offsetSeconds?: number } {
  const v = raw.trim().toUpperCase()
  if (v === 'START' || v === '0%' || v === '00:00:00') return { type: 'preroll', offsetSeconds: 0 }
  if (v === 'END') return { type: 'postroll' }
  if (v.endsWith('%') && durationHint > 0) {
    const pct = Number(v.slice(0, -1))
    return { type: 'midroll', offsetSeconds: (durationHint * pct) / 100 }
  }
  const parts = v.split(':').map(Number)
  if (parts.length === 3) {
    const sec = parts[0] * 3600 + parts[1] * 60 + parts[2]
    return sec <= 0 ? { type: 'preroll', offsetSeconds: 0 } : { type: 'midroll', offsetSeconds: sec }
  }
  const n = Number(v)
  if (Number.isFinite(n)) {
    return n <= 0 ? { type: 'preroll', offsetSeconds: 0 } : { type: 'midroll', offsetSeconds: n }
  }
  return { type: 'midroll', offsetSeconds: 0 }
}

export async function fetchVmap(url: string): Promise<AdBreak[]> {
  const res = await fetch(url, { credentials: 'omit' })
  if (!res.ok) throw new Error(`VMAP HTTP ${res.status}`)
  const xml = await res.text()
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  const breaks: AdBreak[] = []
  const nodes = Array.from(doc.querySelectorAll('vmap\\:AdBreak, AdBreak'))
  let i = 0
  for (const node of nodes) {
    const timeOffset = node.getAttribute('timeOffset') || 'start'
    const { type, offsetSeconds } = parseOffset(timeOffset)
    const vastUrl =
      node.querySelector('vmap\\:AdTagURI, AdTagURI')?.textContent?.trim() ||
      node.querySelector('AdTagURI')?.textContent?.trim()
    const inlineVast = node.querySelector('VAST')
    let breakItem: AdBreak | null = null
    if (vastUrl) {
      breakItem = await fetchVast(vastUrl)
    } else if (inlineVast) {
      breakItem = parseVastXml(new XMLSerializer().serializeToString(inlineVast), `vmap-${i}`)
    }
    if (breakItem) {
      breaks.push({
        ...breakItem,
        id: `vmap-${i}-${type}`,
        type,
        offsetSeconds: type === 'midroll' ? offsetSeconds : undefined,
      })
    }
    i++
  }
  return breaks
}
